import { useRef, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { categories } from '../data/categories';
import GetImageUrl from './GetImageUrl';

export default function ProductCategories() {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const checkScroll = () => {
    if (scrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft + clientWidth < scrollWidth);
    }
  };

  const scroll = (direction: 'left' | 'right') => {
    if (scrollRef.current) {
      const scrollAmount = scrollRef.current.clientWidth / 2;
      scrollRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth',
      });
    }
  };

  useEffect(() => {
    checkScroll();

    const handleResize = () => checkScroll();
    window.addEventListener('resize', handleResize);

    const scrollEl = scrollRef.current; // Capture the ref value

    scrollEl?.addEventListener('scroll', checkScroll);

    // Re-check scroll when images load
    const images = scrollEl?.querySelectorAll('img') || [];
    const onLoad = () => checkScroll();
    images.forEach(img => img.addEventListener('load', onLoad));

    return () => {
      window.removeEventListener('resize', handleResize);
      scrollEl?.removeEventListener('scroll', checkScroll);
      images.forEach(img => img.removeEventListener('load', onLoad));
    };
  }, []);

  return (
    <section className="py-14 bg-gray-50 relative">
      <div className="max-w-FULL mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-slate-900 mb-4">
            Our Product Categories
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Discover our comprehensive range of spare parts designed for reliability and performance
          </p>
        </div>

        <div className="relative">
          {/* Left Arrow */}
          {canScrollLeft && (
            <button
              onClick={() => scroll('left')}
              className="absolute left-0 top-1/2 transform -translate-y-1/2 z-20 bg-slate-800/80 backdrop-blur-sm p-3 rounded-full shadow-lg hover:bg-orange-600 hover:text-white text-white transition"
            >
              <ChevronLeft size={18} />
            </button>
          )}

          {/* Right Arrow */}
          {canScrollRight && (
            <button
              onClick={() => scroll('right')}
              className="absolute right-0 top-1/2 transform -translate-y-1/2 z-20 bg-slate-800/80 backdrop-blur-sm p-3 rounded-full shadow-lg hover:bg-orange-600 hover:text-white text-white transition"
            >
              <ChevronRight size={18} />
            </button>
          )}

          {/* Scrollable container */}
          <div
            ref={scrollRef}
            className="
              flex flex-nowrap space-x-6 overflow-x-auto scroll-smooth
              pb-10
            "
          >
            {categories.map((category) => (
              <Link
                to={`/products?category=${category.id}`}
                key={category.id}
                className="flex-shrink-0 w-72 group bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 cursor-pointer transform hover:-translate-y-2"
              >
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={GetImageUrl(category.image)}
                    alt={category.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                  <div className="absolute top-4 left-4 bg-orange-600 text-white p-3 rounded-xl">
                    {category.icon}
                  </div>
                  <div className="absolute bottom-4 right-4 bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full text-sm font-semibold text-slate-900">
                    {category.count}+ items
                  </div>
                </div>

                <div className="p-6">
                  <h3 className="text-xl font-bold text-slate-900 mb-2 group-hover:text-orange-600 transition-colors">
                    {category.name}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    {category.description}
                  </p>
                  <div className="mt-4 flex items-center text-orange-600 font-medium">
                    <span>Browse Category</span>
                    <svg
                      className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>

      {/* Hide scrollbar */}
      <style>{`
        .scroll-smooth::-webkit-scrollbar { display: none; }
        .scroll-smooth { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>
    </section>
  );
};
